import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  TextFieldProps,
  Typography,
  useTheme,
} from '@mui/material'

import { useGetTemplateMessagesAndPlaceholderskeys } from '@src/data/api/chat-message/chat-message-api'
import { PlaceholderDefinitionDto } from '@src/data/types/TemplateMessageResponseDto'
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react'

interface PlaceholderMentionProps extends Omit<TextFieldProps, 'onChange'> {
  value: string
  onChange: (value: string) => void
  financingProgramIds?: string[]
}

const PlaceholderMention = forwardRef<HTMLDivElement, PlaceholderMentionProps>(
  ({ value, onChange, financingProgramIds = [], ...textFieldProps }, ref) => {
    const theme = useTheme()
    const [templateMessagesWithPlaceholders] = useGetTemplateMessagesAndPlaceholderskeys()
    const allPlaceholders: PlaceholderDefinitionDto[] = templateMessagesWithPlaceholders.placeholders

    // Filter placeholders by selected financing programs (intersection - AND logic)
    const placeholders = useMemo(() => {
      if (!financingProgramIds || financingProgramIds.length === 0) {
        return []
      }
      return allPlaceholders.filter(p =>
        p.financingPrograms.length === 0 || // universal placeholders
        financingProgramIds.every(selectedProgram => p.financingPrograms.includes(selectedProgram)) // only show placeholders applicable to ALL selected programs
      )
    }, [allPlaceholders, financingProgramIds])

    const [showSuggestions, setShowSuggestions] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [mentionStart, setMentionStart] = useState(-1)
    const suggestionsRef = useRef<HTMLDivElement>(null)

    // Filter placeholders based on search term
    const filteredPlaceholders = useMemo(() => {
      if (showSuggestions && searchTerm === '') {
        return placeholders
      }
      if (!searchTerm) {
        return []
      }
      const filtered = placeholders.filter((p) =>
        p.key.toLowerCase().includes(searchTerm.toLowerCase())
      )
      return filtered
    }, [searchTerm, placeholders, showSuggestions]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      onChange(newValue);

      // Find the last { before cursor
      const cursorPos = e.target.selectionStart ?? newValue.length
      const textBeforeCursor = newValue.substring(0, cursorPos)
      const lastBraceIndex = textBeforeCursor.lastIndexOf('{')

      if (lastBraceIndex !== -1) {
        const afterBrace = textBeforeCursor.substring(lastBraceIndex + 1)
        if (!afterBrace.includes(' ') && !afterBrace.includes('\n')) {
          setMentionStart(lastBraceIndex)
          setSearchTerm(afterBrace)
          setShowSuggestions(true)
          setSelectedIndex(0)
          return;
        }
      }
      setShowSuggestions(false)
      setMentionStart(-1)
      setSearchTerm('')
    }

    const insertPlaceholder = (placeholderKey: string) => {
      if (mentionStart === -1) return

      const textBefore = value.substring(0, mentionStart)
      const textAfter = value.substring(mentionStart + 1 + searchTerm.length)
      const newValue = `${textBefore}{${placeholderKey}}${textAfter}`

      onChange(newValue)
      setShowSuggestions(false)
      setSearchTerm('')
      setMentionStart(-1)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showSuggestions || filteredPlaceholders.length === 0) {
        return
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((prev) => (prev + 1) % filteredPlaceholders.length)
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((prev) =>
            prev === 0 ? filteredPlaceholders.length - 1 : prev - 1
          )
          break
        case 'Enter':
          e.preventDefault()
          insertPlaceholder(filteredPlaceholders[selectedIndex].key)
          break
        case 'Escape':
          e.preventDefault()
          setShowSuggestions(false)
          break
        default:
          break
      }
    }

    // Auto-scroll selected item into view
    useEffect(() => {
      if (suggestionsRef.current) {
        const selectedElement = suggestionsRef.current.querySelector(
          `[data-index="${selectedIndex}"]`
        )
        if (selectedElement) {
          selectedElement.scrollIntoView({ block: 'nearest' })
        }
      }
    }, [selectedIndex])

    return (
      <Box ref={ref} sx={{ position: 'relative' }}>
        <TextField
          {...textFieldProps}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          fullWidth
        />

        {showSuggestions && filteredPlaceholders.length > 0 && (
          <Paper
            ref={suggestionsRef}
            elevation={3}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1000,
              mt: 1,
              maxHeight: 300,
              overflow: 'auto',
            }}
          >
            <List sx={{ py: 0 }}>
              {filteredPlaceholders.map((placeholder, index) => (
                <ListItem
                  key={placeholder.key}
                  disablePadding
                  data-index={index}
                  sx={{
                    backgroundColor:
                      index === selectedIndex ? theme.palette.action.hover : 'transparent',
                  }}
                >
                  <ListItemButton
                    dense
                    onClick={() => insertPlaceholder(placeholder.key)}
                    sx={{ py: 1 }}
                  >
                    <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                      <ListItemText
                        primary={`{{${placeholder.key}}}`}
                        slotProps={{
                          primary: {
                            variant: 'body2',
                            sx: { fontFamily: 'monospace', fontWeight: 500 },
                          },
                        }}
                      />
                    </Stack>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {showSuggestions &&
          filteredPlaceholders.length === 0 &&
          searchTerm && (
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 1000,
                mt: 1,
                p: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                No placeholders found
              </Typography>
            </Paper>
          )}
      </Box>
    )
  }
)

PlaceholderMention.displayName = 'PlaceholderMention'

export default PlaceholderMention
